import React, { useState, useEffect } from "react";
import "../../vendor/vendorList.css";
import { ReactComponent as SearchIc } from "../../../assets/svg/search.svg";
import { ReactComponent as AddIc } from "../../../assets/svg/add_icon.svg";
import { ReactComponent as RarIc } from "../../../assets/svg/rar_ic.svg";
import { ReactComponent as LarIc } from "../../../assets/svg/lar_ic.svg";
import { ReactComponent as DownArrowIc } from "../../../assets/svg/down_arrow_ics.svg";
import { Dropdown, Spinner, Toast, ToastContainer } from "react-bootstrap";
import ReactPaginate from "react-paginate";

import { ReactComponent as EditIc } from "../../../assets/svg/edit_ic.svg";
import { ReactComponent as DeleteIc } from "../../../assets/svg/delete_ic.svg";
import { ApprovalConfigCreate } from "./approval-create";
import { ApprovalConfigView } from "./approval-view";
import { getApprovalListApi, getApprovalByIdApi, deleteApprovalApi } from "./approval-api";
import DeleteModal from "../../common/deleteModel";

export const ApprovalConfigList = ({ 
    isCreateMode, 
    setIsApprovalCreate, 
    selectedFlow, 
    setSelectedFlow,
    rawSelectedFlow,
    setRawSelectedFlow
}) => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [search, setSearch] = useState("");
    const [approvalList, setApprovalList] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [isOpenDelete, setIsOpenDelete] = useState(false);

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarStatus, setSnackbarStatus] = useState(false);

    const handleSnackbarClose = () => setShowSnackbar(false);

    const showToast = (message, status = false) => {
        setSnackbarMessage(message);
        setSnackbarStatus(status);
        setShowSnackbar(true);
    };

    const mapApiDataToComponent = (apiItem) => {
        if (!apiItem) return null;
        
        const levels = (apiItem.levels || []).map((lvl) => {
            const persons = (lvl.approvers || []).map((appr) => {
                if (appr.user) {
                    return appr.user.label || appr.user.name;
                } else if (appr.role) {
                    return appr.role.label || appr.role.name;
                }
                return "Unknown";
            }).filter(Boolean);

            return {
                id: lvl.id,
                name: lvl.level_name || `Level ${lvl.level_order}`,
                type: lvl.approver_type || "",
                persons: persons,
                canReject: !!lvl.can_reject,
                commentsMandatory: !!lvl.comments_mandatory
            };
        });

        return {
            id: apiItem.id,
            code: apiItem.code,
            name: apiItem.name,
            module: apiItem.module,
            levelsCount: apiItem.levels_count || levels.length,
            type: apiItem.type,
            status: apiItem.status,
            createdAt: apiItem.created_at || "N/A",
            createdBy: apiItem.created_by_user?.name || "System",
            updatedAt: apiItem.updated_at || "N/A",
            updatedBy: apiItem.updated_by_user?.name || "System",
            levels: levels
        };
    };

    const fetchApprovalList = async () => {
        setLoading(true);
        try {
            const skip = (page - 1) * limit;
            const response = await getApprovalListApi({
                search: search,
                limit: limit,
                skip: skip
            });
            if (response && response.data && response.data.success) {
                setApprovalList(response.data.data || []);
                setTotalRecords(response.data.total_rec || response.data.total || 0);
                setTotalPages(response.data.total_pgs !== undefined ? response.data.total_pgs + 1 : 0);
            } else {
                setApprovalList([]);
                setTotalRecords(0);
                setTotalPages(0);
            }
        } catch (error) {
            console.error("Error loading approval configurations:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchApprovalList();
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [page, limit, search]);

    const handleViewFlow = async (item) => {
        try {
            const response = await getApprovalByIdApi(item.id);
            if (response && response.data && response.data.success) {
                const apiData = response.data.data;
                setRawSelectedFlow(apiData);
                setSelectedFlow(mapApiDataToComponent(apiData));
                setIsApprovalCreate("view");
            } else {
                setSelectedFlow(mapApiDataToComponent(item));
                setIsApprovalCreate("view");
            }
        } catch (error) {
            setSelectedFlow(mapApiDataToComponent(item));
            setIsApprovalCreate("view");
        }
    };

    const handleEditFlow = async (item, e) => {
        if (e) e.stopPropagation();
        try {
            const response = await getApprovalByIdApi(item.id);
            if (response && response.data && response.data.success) {
                const apiData = response.data.data;
                setRawSelectedFlow(apiData);
                setSelectedFlow(mapApiDataToComponent(apiData));
                setIsApprovalCreate("create");
            } else {
                setRawSelectedFlow(item);
                setSelectedFlow(mapApiDataToComponent(item));
                setIsApprovalCreate("create");
            }
        } catch (error) {
            setRawSelectedFlow(item);
            setSelectedFlow(mapApiDataToComponent(item));
            setIsApprovalCreate("create");
        }
    };

    const handleDeleteClick = (id, e) => {
        e.stopPropagation();
        setDeleteId(id);
        setIsOpenDelete(true);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        setLoading(true);
        try {
            const response = await deleteApprovalApi(deleteId);
            if (response && response.data && response.data.success) {
                showToast("Approval Configuration Deleted Successfully", true);
                fetchApprovalList();
            } else {
                showToast(response.data?.message || "Failed to delete");
            }
        } catch (error) {
            showToast("An error occurred");
        } finally {
            setLoading(false);
            setIsOpenDelete(false);
            setDeleteId(null);
        }
    };

    return (
        <>
            {isCreateMode === "create" || isCreateMode === true ? (
                <ApprovalConfigCreate onBack={() => {
                    setIsApprovalCreate(false);
                    setRawSelectedFlow(null);
                    setSelectedFlow(null);
                }} editData={rawSelectedFlow} />
            ) : isCreateMode === "view" ? (
                <ApprovalConfigView
                    flowData={selectedFlow}
                    onBack={() => setIsApprovalCreate(false)}
                    onEdit={() => setIsApprovalCreate("create")}
                />
            ) : (
                <>
                    <div className="bg-white shadow-sm"
                        style={{
                            width: "100%",
                            minHeight: "calc(100vh - 260px)",
                            borderBottomRightRadius: "16px",
                            borderBottomLeftRadius: "16px",
                            padding: "16px",
                            boxShadow: "0px 2px 4px 0px #0000000A",
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                            overflow: "auto"
                        }}>

                        {/* Top Header */}
                        <div className="d-flex align-items-center justify-content-between"
                            style={{ width: "100%", height: "40px" }}>
                            <p className="m-0 textGrey fs-13 fw-500">Total counts : {totalRecords}</p>
                            <div className="d-flex gap-3 h-100 align-items-center">
                                {/* Search */}
                                <div className="search-wrapper d-flex align-items-center gap-2 px-3 py-2">
                                    <SearchIc style={{ width: "18px", height: "18px", color: "#9CA3AF" }} />
                                    <input
                                        type="text"
                                        placeholder="Search..."
                                        className="border-0 outline-0 rounded-3 fs-14 fw-500 textcolor"
                                        value={search}
                                        onChange={(e) => {
                                            setSearch(e.target.value);
                                            setPage(1);
                                        }}
                                    />
                                </div>
                                {/* Add New Flow */}
                                <div className="cursor-pointer d-flex align-items-center justify-content-center gap-2 px-4"
                                    style={{ backgroundColor: "#4F46E5", borderRadius: "20px", color: "white", height: "40px" }}
                                    onClick={() => {
                                        setSelectedFlow(null);
                                        setRawSelectedFlow(null);
                                        setIsApprovalCreate(true);
                                    }}>
                                    <AddIc width={14} height={14} fill="white" />
                                    <div className="fs-14 fw-700">New Approval Flow</div>
                                </div>
                            </div>
                        </div>

                        {/* Table Header */}
                        <div className="d-flex align-items-center"
                            style={{ backgroundColor: "#F2F2F2", borderRadius: "4px 4px 0 0", width: "100%", height: "48px", padding: "0 16px", gap: "16px" }}>
                            <div style={{ width: "40px" }} className="d-flex align-items-center">
                                <input type="checkbox" className="form-check-input" style={{ width: "16px", height: "16px" }} />
                            </div>
                            <div style={{ flex: "1.5" }} className="fs-13 fw-700 textcolor">Code</div>
                            <div style={{ flex: "2.5" }} className="fs-13 fw-700 textcolor">Name</div>
                            <div style={{ flex: "2" }} className="fs-13 fw-700 textcolor">Module</div>
                            <div style={{ flex: "1" }} className="fs-13 fw-700 textcolor">Levels</div>
                            <div style={{ flex: "1.5" }} className="fs-13 fw-700 textcolor">Type</div>
                            <div style={{ flex: "1.5" }} className="fs-13 fw-700 textcolor">Status</div>
                        </div>

                        {/* Table Body */}
                        <div style={{ width: "100%", minHeight: "200px" }}>
                            {loading ? (
                                <div className="d-flex align-items-center justify-content-center" style={{ height: "200px" }}>
                                    <Spinner animation="border" variant="primary" />
                                </div>
                            ) : approvalList.length === 0 ? (
                                <div className="d-flex align-items-center justify-content-center textGrey fs-14 fw-500" style={{ height: "200px" }}>
                                    No approval configurations found.
                                </div>
                            ) : (
                                approvalList.map((item, index) => (
                                    <div
                                        key={item.id || index}
                                        className="d-flex align-items-center position-relative"
                                        style={{
                                            backgroundColor: hoveredIndex === index ? "#FAFAFA" : "#FFFFFF",
                                            cursor: "pointer",
                                            width: "100%",
                                            height: "40px",
                                            padding: "14px 16px",
                                            borderBottom: "0.6px solid #D4DDE7",
                                            gap: "16px"
                                        }}
                                        onMouseEnter={() => setHoveredIndex(index)}
                                        onMouseLeave={() => setHoveredIndex(null)}
                                        onClick={() => handleViewFlow(item)}
                                    >
                                        <div style={{ width: "40px" }} className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
                                            <input type="checkbox" className="form-check-input" style={{ width: "16px", height: "16px" }} />
                                        </div>
                                        <div style={{ flex: "1.5" }} className="fs-13 fw-500 textcolor">{item.code}</div>
                                        <div style={{ flex: "2.5" }} className="fs-13 fw-600 textcolor">{item.name}</div>
                                        <div style={{ flex: "2" }} className="fs-13 fw-500 textcolor">{item.module}</div>
                                        <div style={{ flex: "1" }} className="fs-13 fw-500 textcolor">{item.levels_count || item.levels}</div>
                                        <div style={{ flex: "1.5" }} className="fs-13 fw-500 textcolor">{item.type}</div>
                                        <div style={{ flex: "1.5" }} className="d-flex justify-content-start">
                                            <div className="px-3 py-1 rounded-pill d-flex align-items-center gap-2"
                                                style={{ 
                                                    backgroundColor: item.status === "Active" ? "#DCFCE7" : "#F3F4F6", 
                                                    color: item.status === "Active" ? "#166534" : "#4B5563", 
                                                    fontSize: "12px", 
                                                    fontWeight: "600", 
                                                    border: "none" 
                                                }}>
                                                <div style={{ 
                                                    width: "6px", 
                                                    height: "6px", 
                                                    borderRadius: "50%", 
                                                    backgroundColor: item.status === "Active" ? "#22C55E" : "#9CA3AF" 
                                                }}></div>
                                                {item.status}
                                            </div>
                                        </div>

                                        {/* Action Icons on Hover */}
                                        {hoveredIndex === index && (
                                            <div className="d-flex gap-2"
                                                style={{
                                                    position: "absolute",
                                                    right: "20px",
                                                    top: "50%",
                                                    transform: "translateY(-50%)",
                                                    zIndex: 10
                                                }}>
                                                <div className="rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm border cursor-pointer hover-effect"
                                                    style={{ width: "32px", height: "32px", borderColor: "#E2E8F0" }}
                                                    onClick={(e) => handleEditFlow(item, e)}>
                                                    <EditIc width={16} height={16} />
                                                </div>
                                                <div className="rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm border cursor-pointer hover-effect"
                                                    style={{ width: "32px", height: "32px", borderColor: "#E2E8F0" }}
                                                    onClick={(e) => handleDeleteClick(item.id, e)}>
                                                    <DeleteIc width={16} height={16} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Pagination Footer */}
                        {approvalList.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-auto px-2">
                                <Dropdown>
                                    <span className="pagination-color fs-13 fw-500">
                                        Records per page: &nbsp;
                                    </span>
                                    <Dropdown.Toggle
                                        id="dropdown-basic"
                                        className="bg-transparent pagination-color border-0 custom-dropdown-toggle p-0"
                                    >
                                        <span className="bg-white textcolor no-of-rows rounded-3 px-2 py-2">
                                            {limit} <DownArrowIc />
                                        </span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu
                                        className="my-2"
                                        style={{ minWidth: "unset", width: "40%" }}
                                    >
                                        {[10, 25, 50, 75, 100].map((size) => (
                                            <Dropdown.Item
                                                key={size}
                                                onClick={() => {
                                                    setLimit(size);
                                                    setPage(1);
                                                }}
                                                active={limit === size}
                                                className="text-center"
                                            >
                                                {size}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>

                                <div className="d-flex align-items-center gap-2">
                                    <ReactPaginate
                                        previousLabel={
                                            <div
                                                className="rounded-5 bg-white d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: "32px",
                                                    height: "32px",
                                                }}
                                            >
                                                <LarIc />
                                            </div>
                                        }
                                        nextLabel={
                                            <div
                                                className="rounded-5 bg-white d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: "32px",
                                                    height: "32px",
                                                }}
                                            >
                                                <RarIc />
                                            </div>
                                        }
                                        breakLabel={"..."}
                                        pageCount={totalPages}
                                        marginPagesDisplayed={2}
                                        pageRangeDisplayed={3}
                                        onPageChange={(event) => setPage(event.selected + 1)}
                                        forcePage={Math.max(0, page - 1)}
                                        containerClassName={"pagination"}
                                        activeClassName={"active"}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Delete confirmation modal */}
                    <DeleteModal
                        title="Approval Configuration"
                        isOpen={isOpenDelete}
                        onClose={() => setIsOpenDelete(false)}
                        onDelete={confirmDelete}
                    />
                </>
            )}

            <ToastContainer position="top-end">
                <Toast className="d-inline-block m-1" bg={snackbarStatus === true ? "success" : "danger"} show={showSnackbar} onClose={handleSnackbarClose} delay={3000} autohide>
                    <Toast.Body style={{ color: "#ffffff" }}>{snackbarMessage}</Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};
